import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Settings,
  Plus,
  Loader2,
  Edit,
  Trash2,
  Phone,
  MessageCircle,
  Building2,
  Clock,
  Shield,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Copy,
  Eye,
  EyeOff,
  Zap,
  Send,
  Smartphone,
  QrCode,
  ArrowRight
} from "lucide-react";

const DAYS_OF_WEEK = [
  { id: 1, name: "Lunes" },
  { id: 2, name: "Martes" },
  { id: 3, name: "Miércoles" },
  { id: 4, name: "Jueves" },
  { id: 5, name: "Viernes" },
  { id: 6, name: "Sábado" },
  { id: 7, name: "Domingo" }
];

export default function WhatsAppConfigPage() {
  const [configs, setConfigs] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showSandboxDialog, setShowSandboxDialog] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [sandboxStep, setSandboxStep] = useState(1);
  const [sandboxCredentials, setSandboxCredentials] = useState({
    account_sid: "",
    auth_token: ""
  });
  const [creatingSandbox, setCreatingSandbox] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testMessage, setTestMessage] = useState("¡Hola! Este es un mensaje de prueba desde QAsesores. 🎉");
  const [sendingTest, setSendingTest] = useState(false);
  const [selectedConfigForTest, setSelectedConfigForTest] = useState(null);
  const [editingConfig, setEditingConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showTokens, setShowTokens] = useState({});
  const { getAuthHeaders, user } = useAuth();

  const [formData, setFormData] = useState({
    provider: "meta",
    name: "",
    phone_number: "",
    is_active: true,
    // Meta fields
    meta_phone_number_id: "",
    meta_access_token: "",
    meta_business_account_id: "",
    meta_verify_token: "",
    // Twilio fields
    twilio_account_sid: "",
    twilio_auth_token: "",
    twilio_phone_number: "",
    // 360dialog fields
    dialog360_api_key: "",
    dialog360_channel_id: "",
    // Common
    departments: [],
    auto_response_enabled: false,
    welcome_message: "",
    out_of_hours_message: "",
    business_hours_start: "09:00",
    business_hours_end: "18:00",
    business_days: [1, 2, 3, 4, 5],
    calls_enabled: false
  });

  const fetchConfigs = useCallback(async () => {
    try {
      setLoading(true);
      const [configsRes, deptsRes] = await Promise.all([
        axios.get(`${API}/whatsapp/config`, getAuthHeaders()),
        axios.get(`${API}/departamentos`, getAuthHeaders())
      ]);
      setConfigs(configsRes.data || []);
      setDepartments(deptsRes.data || []);
    } catch (err) {
      console.error("Error:", err);
      toast.error("Error al cargar configuraciones");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const resetForm = () => {
    setFormData({
      provider: "meta",
      name: "",
      phone_number: "",
      is_active: true,
      meta_phone_number_id: "",
      meta_access_token: "",
      meta_business_account_id: "",
      meta_verify_token: "",
      twilio_account_sid: "",
      twilio_auth_token: "",
      twilio_phone_number: "",
      dialog360_api_key: "",
      dialog360_channel_id: "",
      departments: [],
      auto_response_enabled: false,
      welcome_message: "",
      out_of_hours_message: "",
      business_hours_start: "09:00",
      business_hours_end: "18:00",
      business_days: [1, 2, 3, 4, 5],
      calls_enabled: false
    });
    setEditingConfig(null);
  };

  const openNewDialog = () => {
    resetForm();
    // Generate a random verify token for Meta
    setFormData(prev => ({
      ...prev,
      meta_verify_token: `verify_${Math.random().toString(36).substring(2, 15)}`
    }));
    setShowDialog(true);
  };

  const openEditDialog = (config) => {
    setEditingConfig(config);
    setFormData({
      provider: config.provider || "meta",
      name: config.name || "",
      phone_number: config.phone_number || "",
      is_active: config.is_active !== false,
      meta_phone_number_id: config.meta_phone_number_id || "",
      meta_access_token: config.meta_access_token || "",
      meta_business_account_id: config.meta_business_account_id || "",
      meta_verify_token: config.meta_verify_token || "",
      twilio_account_sid: config.twilio_account_sid || "",
      twilio_auth_token: config.twilio_auth_token || "",
      twilio_phone_number: config.twilio_phone_number || "",
      dialog360_api_key: config.dialog360_api_key || "",
      dialog360_channel_id: config.dialog360_channel_id || "",
      departments: config.departments || [],
      auto_response_enabled: config.auto_response_enabled || false,
      welcome_message: config.welcome_message || "",
      out_of_hours_message: config.out_of_hours_message || "",
      business_hours_start: config.business_hours_start || "09:00",
      business_hours_end: config.business_hours_end || "18:00",
      business_days: config.business_days || [1, 2, 3, 4, 5],
      calls_enabled: config.calls_enabled || false
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.phone_number) {
      toast.error("Nombre y número de teléfono son requeridos");
      return;
    }

    if (formData.provider === "meta" && !formData.meta_phone_number_id) {
      toast.error("Phone Number ID de Meta es requerido");
      return;
    }

    if (formData.provider === "twilio" && !formData.twilio_account_sid) {
      toast.error("Account SID de Twilio es requerido");
      return;
    }

    if (formData.provider === "360dialog" && !formData.dialog360_api_key) {
      toast.error("API Key de 360dialog es requerido");
      return;
    }

    try {
      setSaving(true);
      if (editingConfig) {
        await axios.put(`${API}/whatsapp/config/${editingConfig.id}`, formData, getAuthHeaders());
        toast.success("Configuración actualizada");
      } else {
        await axios.post(`${API}/whatsapp/config`, formData, getAuthHeaders());
        toast.success("Configuración creada");
      }
      setShowDialog(false);
      fetchConfigs();
    } catch (err) {
      console.error("Error:", err);
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (configId) => {
    if (!confirm("¿Está seguro de eliminar esta configuración?")) return;
    try {
      await axios.delete(`${API}/whatsapp/config/${configId}`, getAuthHeaders());
      toast.success("Configuración eliminada");
      fetchConfigs();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado al portapapeles");
  };

  // Sandbox de Twilio functions
  const openSandboxDialog = () => {
    setSandboxStep(1);
    setSandboxCredentials({ account_sid: "", auth_token: "" });
    setShowSandboxDialog(true);
  };

  const createSandboxConfig = async () => {
    if (!sandboxCredentials.account_sid || !sandboxCredentials.auth_token) {
      toast.error("Por favor ingrese Account SID y Auth Token");
      return;
    }

    try {
      setCreatingSandbox(true);
      const sandboxConfig = {
        provider: "twilio",
        name: "Twilio Sandbox (Demo)",
        phone_number: "+14155238886",
        is_active: true,
        twilio_account_sid: sandboxCredentials.account_sid,
        twilio_auth_token: sandboxCredentials.auth_token,
        twilio_phone_number: "+14155238886",
        departments: [],
        auto_response_enabled: true,
        welcome_message: "¡Hola! Gracias por contactarnos a través de WhatsApp. Este es un mensaje automático del sandbox de prueba. 🎉",
        out_of_hours_message: "",
        business_hours_start: "00:00",
        business_hours_end: "23:59",
        business_days: [1, 2, 3, 4, 5, 6, 7]
      };

      await axios.post(`${API}/whatsapp/config`, sandboxConfig, getAuthHeaders());
      toast.success("¡Sandbox de Twilio configurado exitosamente!");
      setShowSandboxDialog(false);
      fetchConfigs();
    } catch (err) {
      console.error("Error:", err);
      toast.error(err.response?.data?.detail || "Error al crear configuración del sandbox");
    } finally {
      setCreatingSandbox(false);
    }
  };

  const openTestDialog = (config) => {
    setSelectedConfigForTest(config);
    setTestPhone("");
    setTestMessage("¡Hola! Este es un mensaje de prueba desde QAsesores. 🎉");
    setShowTestDialog(true);
  };

  const sendTestMessage = async () => {
    if (!testPhone || !testMessage) {
      toast.error("Por favor ingrese número y mensaje");
      return;
    }

    try {
      setSendingTest(true);
      await axios.post(`${API}/whatsapp/send`, {
        config_id: selectedConfigForTest.id,
        recipient_phone: testPhone,
        message_text: testMessage
      }, getAuthHeaders());
      toast.success("¡Mensaje de prueba enviado exitosamente!");
      setShowTestDialog(false);
    } catch (err) {
      console.error("Error:", err);
      const errorMsg = err.response?.data?.detail || "Error al enviar mensaje";
      if (errorMsg.includes("Sandbox") || errorMsg.includes("registrado")) {
        toast.error(errorMsg, { duration: 8000 });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setSendingTest(false);
    }
  };

  const getWebhookUrl = (provider, configId) => {
    const baseUrl = window.location.origin.replace("localhost:3000", API.replace("/api", "").replace("https://", "").replace("http://", ""));
    if (provider === "meta") {
      return `${API.replace("/api", "")}/api/whatsapp/webhook/meta`;
    } else if (provider === "360dialog") {
      return `${API.replace("/api", "")}/api/whatsapp/webhook/360dialog`;
    }
    return `${API.replace("/api", "")}/api/whatsapp/webhook/twilio`;
  };

  const isAdmin = user?.role?.toLowerCase() === "admin" || user?.role?.toLowerCase() === "administrador";

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="whatsapp-config-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Configuración WhatsApp
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Configura tus números de WhatsApp Business
              </p>
            </div>
          </div>
          {isAdmin && (
            <Button 
              onClick={openNewDialog}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="new-config-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva Configuración
            </Button>
          )}
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {/* Sandbox Card - Destacado */}
          <Card className="border-green-300 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border-2">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-green-900 dark:text-green-100">Twilio Sandbox</h3>
                    <Badge className="bg-green-500 text-xs">GRATIS</Badge>
                  </div>
                  <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                    Prueba WhatsApp inmediatamente sin configuración compleja.
                  </p>
                  {isAdmin && (
                    <Button 
                      onClick={openSandboxDialog}
                      size="sm"
                      className="mt-3 bg-green-600 hover:bg-green-700 text-white"
                      data-testid="setup-sandbox-button"
                    >
                      <Zap className="w-3 h-3 mr-1" />
                      Configurar Sandbox
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-blue-200 bg-blue-50 dark:bg-blue-900/20">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-blue-900 dark:text-blue-100">Meta Business API</h3>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                    Producción profesional con verificación de negocio.
                  </p>
                  <a 
                    href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline flex items-center gap-1 mt-2"
                  >
                    Documentación <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-purple-200 bg-purple-50 dark:bg-purple-900/20">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-purple-900 dark:text-purple-100">Twilio Producción</h3>
                  <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
                    Número propio de Twilio para producción.
                  </p>
                  <a 
                    href="https://www.twilio.com/docs/whatsapp/api" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-purple-600 hover:underline flex items-center gap-1 mt-2"
                  >
                    Documentación <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Configurations List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : configs.length === 0 ? (
          <Card className="border-dashed border-2">
            <CardContent className="py-12 text-center">
              <MessageCircle className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 mb-2">No hay configuraciones</h3>
              <p className="text-slate-500 mb-4">Agrega tu primera configuración de WhatsApp Business</p>
              {isAdmin && (
                <Button onClick={openNewDialog} className="bg-green-600 hover:bg-green-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Configuración
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {configs.map((config) => (
              <Card key={config.id} className="hover:shadow-lg transition-shadow" data-testid={`config-card-${config.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        config.provider === "meta" ? "bg-blue-500" : config.provider === "360dialog" ? "bg-green-500" : "bg-purple-500"
                      }`}>
                        <MessageCircle className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{config.name}</CardTitle>
                        <p className="text-sm text-slate-500">{config.phone_number}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={config.is_active ? "default" : "secondary"}>
                        {config.is_active ? "Activo" : "Inactivo"}
                      </Badge>
                      <Badge variant="outline" className={
                        config.provider === "meta" ? "border-blue-300 text-blue-600" : config.provider === "360dialog" ? "border-green-300 text-green-600" : "border-purple-300 text-purple-600"
                      }>
                        {config.provider === "meta" ? "Meta API" : config.provider === "360dialog" ? "360dialog" : "Twilio"}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Webhook URL */}
                    <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">Webhook URL (para configurar en {config.provider === "meta" ? "Meta" : config.provider === "360dialog" ? "360dialog" : "Twilio"})</p>
                      <div className="flex items-center gap-2">
                        <code className="text-xs flex-1 overflow-hidden text-ellipsis">
                          {getWebhookUrl(config.provider, config.id)}
                        </code>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => copyToClipboard(getWebhookUrl(config.provider, config.id))}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>

                    {/* Verify Token for Meta */}
                    {config.provider === "meta" && config.meta_verify_token && (
                      <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                        <p className="text-xs text-amber-600 mb-1">Verify Token (para webhook de Meta)</p>
                        <div className="flex items-center gap-2">
                          <code className="text-xs flex-1">{config.meta_verify_token}</code>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyToClipboard(config.meta_verify_token)}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Departments */}
                    {config.departments?.length > 0 && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-600">
                          {config.departments.length} departamento(s) asignado(s)
                        </span>
                      </div>
                    )}

                    {/* Auto-response status */}
                    <div className="flex items-center gap-2">
                      {config.auto_response_enabled ? (
                        <>
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-sm text-green-600">Respuestas automáticas activas</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-500">Sin respuestas automáticas</span>
                        </>
                      )}
                    </div>

                    {/* Calls status */}
                    <div className="flex items-center gap-2">
                      {config.calls_enabled ? (
                        <>
                          <Phone className="w-4 h-4 text-blue-500" />
                          <span className="text-sm text-blue-600">Llamadas habilitadas</span>
                        </>
                      ) : (
                        <>
                          <Phone className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-500">Llamadas deshabilitadas</span>
                        </>
                      )}
                    </div>

                    {/* Business Hours */}
                    {config.business_hours_start && config.business_hours_end && (
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-600">
                          {config.business_hours_start} - {config.business_hours_end}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {isAdmin && (
                    <div className="flex gap-2 mt-4 pt-4 border-t">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => openEditDialog(config)}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="flex-1 text-green-600 hover:bg-green-50 border-green-300"
                        onClick={() => openTestDialog(config)}
                        data-testid={`test-button-${config.id}`}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Probar
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(config.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingConfig ? "Editar Configuración" : "Nueva Configuración de WhatsApp"}
              </DialogTitle>
              <DialogDescription>
                Configure los datos de conexión con WhatsApp Business
              </DialogDescription>
            </DialogHeader>

            <Tabs value={formData.provider} onValueChange={(v) => setFormData({...formData, provider: v})}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="meta" className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Meta API
                </TabsTrigger>
                <TabsTrigger value="twilio" className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Twilio
                </TabsTrigger>
                <TabsTrigger value="360dialog" className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  360dialog
                </TabsTrigger>
              </TabsList>

              <div className="space-y-4 mt-4">
                {/* Common Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nombre de la configuración *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Ej: WhatsApp Principal"
                    />
                  </div>
                  <div>
                    <Label>Número de teléfono *</Label>
                    <Input
                      value={formData.phone_number}
                      onChange={(e) => setFormData({...formData, phone_number: e.target.value})}
                      placeholder="+1 809 555 1234"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(v) => setFormData({...formData, is_active: v})}
                  />
                  <Label>Configuración activa</Label>
                </div>

                {/* Meta API Fields */}
                <TabsContent value="meta" className="space-y-4 mt-0">
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-3">Credenciales de Meta</h4>
                    <div className="space-y-3">
                      <div>
                        <Label>Phone Number ID *</Label>
                        <Input
                          value={formData.meta_phone_number_id}
                          onChange={(e) => setFormData({...formData, meta_phone_number_id: e.target.value})}
                          placeholder="Ej: 102349385043874573"
                        />
                      </div>
                      <div>
                        <Label>Access Token *</Label>
                        <div className="relative">
                          <Input
                            type={showTokens.meta ? "text" : "password"}
                            value={formData.meta_access_token}
                            onChange={(e) => setFormData({...formData, meta_access_token: e.target.value})}
                            placeholder="Token de acceso permanente"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0"
                            onClick={() => setShowTokens({...showTokens, meta: !showTokens.meta})}
                          >
                            {showTokens.meta ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                      <div>
                        <Label>Business Account ID</Label>
                        <Input
                          value={formData.meta_business_account_id}
                          onChange={(e) => setFormData({...formData, meta_business_account_id: e.target.value})}
                          placeholder="Ej: 106181168862417"
                        />
                      </div>
                      <div>
                        <Label>Verify Token (para webhook)</Label>
                        <div className="flex gap-2">
                          <Input
                            value={formData.meta_verify_token}
                            onChange={(e) => setFormData({...formData, meta_verify_token: e.target.value})}
                            placeholder="Token de verificación"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => copyToClipboard(formData.meta_verify_token)}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">Use este token al configurar el webhook en Meta</p>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Twilio Fields */}
                <TabsContent value="twilio" className="space-y-4 mt-0">
                  <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <h4 className="font-medium text-purple-900 dark:text-purple-100 mb-3">Credenciales de Twilio</h4>
                    <div className="space-y-3">
                      <div>
                        <Label>Account SID *</Label>
                        <Input
                          value={formData.twilio_account_sid}
                          onChange={(e) => setFormData({...formData, twilio_account_sid: e.target.value})}
                          placeholder="Ej: ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                        />
                      </div>
                      <div>
                        <Label>Auth Token *</Label>
                        <div className="relative">
                          <Input
                            type={showTokens.twilio ? "text" : "password"}
                            value={formData.twilio_auth_token}
                            onChange={(e) => setFormData({...formData, twilio_auth_token: e.target.value})}
                            placeholder="Token de autenticación"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0"
                            onClick={() => setShowTokens({...showTokens, twilio: !showTokens.twilio})}
                          >
                            {showTokens.twilio ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                      <div>
                        <Label>Número de Twilio WhatsApp</Label>
                        <Input
                          value={formData.twilio_phone_number}
                          onChange={(e) => setFormData({...formData, twilio_phone_number: e.target.value})}
                          placeholder="Ej: +14155238886 (Sandbox)"
                        />
                        <p className="text-xs text-slate-500 mt-1">Para sandbox use: +14155238886</p>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* 360dialog Fields */}
                <TabsContent value="360dialog" className="space-y-4 mt-0">
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <h4 className="font-medium text-green-900 dark:text-green-100 mb-3">Credenciales de 360dialog</h4>
                    <div className="space-y-3">
                      <div>
                        <Label>API Key *</Label>
                        <div className="relative">
                          <Input
                            type={showTokens.dialog360 ? "text" : "password"}
                            value={formData.dialog360_api_key}
                            onChange={(e) => setFormData({...formData, dialog360_api_key: e.target.value})}
                            placeholder="Tu API Key de 360dialog"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0"
                            onClick={() => setShowTokens({...showTokens, dialog360: !showTokens.dialog360})}
                          >
                            {showTokens.dialog360 ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                      <div>
                        <Label>Channel ID (opcional)</Label>
                        <Input
                          value={formData.dialog360_channel_id}
                          onChange={(e) => setFormData({...formData, dialog360_channel_id: e.target.value})}
                          placeholder="ID del canal de WhatsApp"
                        />
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-green-100 dark:bg-green-800/30 rounded-lg">
                      <p className="text-sm text-green-800 dark:text-green-200">
                        <strong>¿Cómo obtener credenciales?</strong>
                      </p>
                      <ol className="text-xs text-green-700 dark:text-green-300 mt-2 space-y-1 list-decimal list-inside">
                        <li>Inicia sesión en <a href="https://hub.360dialog.com" target="_blank" rel="noopener noreferrer" className="underline">360dialog Hub</a></li>
                        <li>Ve a tu número de WhatsApp</li>
                        <li>Copia el API Key desde la configuración</li>
                        <li>Configura el webhook con la URL proporcionada</li>
                      </ol>
                    </div>
                  </div>
                </TabsContent>

                {/* Departments */}
                <div>
                  <Label className="mb-2 block">Departamentos con acceso</Label>
                  <div className="grid grid-cols-2 gap-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg max-h-40 overflow-y-auto">
                    {departments.map((dept) => (
                      <div key={dept.id} className="flex items-center gap-2">
                        <Checkbox
                          checked={formData.departments.includes(dept.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({...formData, departments: [...formData.departments, dept.id]});
                            } else {
                              setFormData({...formData, departments: formData.departments.filter(d => d !== dept.id)});
                            }
                          }}
                        />
                        <span className="text-sm">{dept.name || dept.nombre}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Auto Responses */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.auto_response_enabled}
                      onCheckedChange={(v) => setFormData({...formData, auto_response_enabled: v})}
                    />
                    <Label>Habilitar respuestas automáticas</Label>
                  </div>

                  {formData.auto_response_enabled && (
                    <div className="space-y-3 pl-4 border-l-2 border-green-300">
                      <div>
                        <Label>Mensaje de bienvenida</Label>
                        <Textarea
                          value={formData.welcome_message}
                          onChange={(e) => setFormData({...formData, welcome_message: e.target.value})}
                          placeholder="Hola! Gracias por contactarnos..."
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label>Mensaje fuera de horario</Label>
                        <Textarea
                          value={formData.out_of_hours_message}
                          onChange={(e) => setFormData({...formData, out_of_hours_message: e.target.value})}
                          placeholder="Gracias por escribirnos. Nuestro horario de atención es..."
                          rows={2}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Calls */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.calls_enabled}
                      onCheckedChange={(v) => setFormData({...formData, calls_enabled: v})}
                      data-testid="calls-enabled-switch"
                    />
                    <Label>Habilitar llamadas a clientes</Label>
                  </div>
                  {formData.calls_enabled && (
                    <p className="text-xs text-slate-500 pl-4 border-l-2 border-blue-300">
                      Se mostrará un botón de llamada en la bandeja de conversaciones para contactar a los clientes directamente.
                    </p>
                  )}
                </div>

                {/* Business Hours */}
                <div>
                  <Label className="mb-2 block">Horario de atención</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">Hora inicio</Label>
                      <Input
                        type="time"
                        value={formData.business_hours_start}
                        onChange={(e) => setFormData({...formData, business_hours_start: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Hora fin</Label>
                      <Input
                        type="time"
                        value={formData.business_hours_end}
                        onChange={(e) => setFormData({...formData, business_hours_end: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {DAYS_OF_WEEK.map((day) => (
                      <Badge
                        key={day.id}
                        variant={formData.business_days.includes(day.id) ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => {
                          if (formData.business_days.includes(day.id)) {
                            setFormData({...formData, business_days: formData.business_days.filter(d => d !== day.id)});
                          } else {
                            setFormData({...formData, business_days: [...formData.business_days, day.id].sort()});
                          }
                        }}
                      >
                        {day.name.substring(0, 3)}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </Tabs>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingConfig ? "Guardar Cambios" : "Crear Configuración"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Sandbox Setup Dialog */}
        <Dialog open={showSandboxDialog} onOpenChange={setShowSandboxDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-green-500" />
                Configurar Twilio Sandbox
              </DialogTitle>
              <DialogDescription>
                Prueba WhatsApp de forma gratuita en minutos
              </DialogDescription>
            </DialogHeader>

            {sandboxStep === 1 && (
              <div className="space-y-4">
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200">
                  <h4 className="font-semibold text-amber-800 dark:text-amber-200 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Paso 1: Obtener credenciales de Twilio
                  </h4>
                  <ol className="mt-3 space-y-2 text-sm text-amber-700 dark:text-amber-300">
                    <li className="flex items-start gap-2">
                      <span className="font-bold">1.</span>
                      <span>
                        Crea una cuenta gratuita en{" "}
                        <a 
                          href="https://www.twilio.com/try-twilio" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-amber-600 underline font-medium"
                        >
                          twilio.com/try-twilio
                        </a>
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold">2.</span>
                      <span>Ve a la consola de Twilio</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold">3.</span>
                      <span>Copia tu <strong>Account SID</strong> y <strong>Auth Token</strong></span>
                    </li>
                  </ol>
                </div>

                <div className="space-y-3">
                  <div>
                    <Label>Account SID</Label>
                    <Input
                      value={sandboxCredentials.account_sid}
                      onChange={(e) => setSandboxCredentials({...sandboxCredentials, account_sid: e.target.value})}
                      placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    />
                  </div>
                  <div>
                    <Label>Auth Token</Label>
                    <Input
                      type="password"
                      value={sandboxCredentials.auth_token}
                      onChange={(e) => setSandboxCredentials({...sandboxCredentials, auth_token: e.target.value})}
                      placeholder="Tu Auth Token de Twilio"
                    />
                  </div>
                </div>

                <Button 
                  className="w-full" 
                  onClick={() => setSandboxStep(2)}
                  disabled={!sandboxCredentials.account_sid || !sandboxCredentials.auth_token}
                >
                  Continuar <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}

            {sandboxStep === 2 && (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-green-800 dark:text-green-200 flex items-center gap-2">
                    <Smartphone className="w-4 h-4" />
                    Paso 2: Activar el Sandbox en tu WhatsApp
                  </h4>
                  <div className="mt-3 space-y-3">
                    <p className="text-sm text-green-700 dark:text-green-300">
                      Para recibir mensajes del sandbox, debes activarlo desde tu teléfono:
                    </p>
                    <div className="bg-white dark:bg-slate-800 p-3 rounded-lg border">
                      <p className="text-sm font-medium mb-2">Envía este mensaje por WhatsApp:</p>
                      <div className="flex items-center gap-2">
                        <code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded text-sm font-mono">
                          join &lt;tu-código&gt;
                        </code>
                        <span className="text-xs text-slate-500">(busca el código en tu consola de Twilio)</span>
                      </div>
                      <p className="text-sm mt-2">
                        Al número: <strong className="text-green-600">+1 415 523 8886</strong>
                      </p>
                    </div>
                    <a 
                      href="https://www.twilio.com/console/sms/whatsapp/sandbox" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-green-600 hover:underline flex items-center gap-1"
                    >
                      Ver mi código de sandbox en Twilio <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setSandboxStep(1)} className="flex-1">
                    Atrás
                  </Button>
                  <Button 
                    onClick={createSandboxConfig} 
                    disabled={creatingSandbox}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    {creatingSandbox && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Crear Configuración
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Test Message Dialog */}
        <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-green-500" />
                Enviar Mensaje de Prueba
              </DialogTitle>
              <DialogDescription>
                Envía un mensaje de WhatsApp para probar la configuración
              </DialogDescription>
            </DialogHeader>

            {selectedConfigForTest && (
              <div className="space-y-4">
                <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Configuración: <strong>{selectedConfigForTest.name}</strong>
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Número: {selectedConfigForTest.phone_number}
                  </p>
                </div>

                {selectedConfigForTest.provider === "twilio" && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-700 dark:text-amber-300 mb-2">
                      <AlertCircle className="w-4 h-4 inline mr-1" />
                      <strong>Importante (Twilio Sandbox):</strong>
                    </p>
                    <ul className="text-xs text-amber-600 dark:text-amber-400 list-disc list-inside space-y-1">
                      <li>El destinatario debe unirse primero al Sandbox</li>
                      <li>Debe enviar el mensaje "join &lt;código&gt;" al número de Sandbox</li>
                      <li>Solo después de unirse podrá recibir mensajes</li>
                    </ul>
                  </div>
                )}

                <div>
                  <Label>Número de destino</Label>
                  <Input
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    placeholder="+1809XXXXXXX"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Incluye el código de país (ej: +1, +52, +34)
                  </p>
                </div>

                <div>
                  <Label>Mensaje</Label>
                  <Textarea
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    placeholder="Escribe tu mensaje de prueba..."
                    rows={3}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTestDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={sendTestMessage} 
                disabled={sendingTest || !testPhone || !testMessage}
                className="bg-green-600 hover:bg-green-700"
              >
                {sendingTest && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Enviar Mensaje
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
